import React from 'react';
import { isPast } from 'date-fns';
import millify from 'millify';
import type { Maybe } from '@soundxyz/utils';
import { useTimer } from '@components-shared/src/countdown/hooks/useTimer';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { GetNotifiedButton } from '@components/EmailNotifications/GetNotifiedButton';
import HeroCountdown from '@components/Landing/HeroCountdown';
import { shouldShowEndsInCountdown } from '@components/SongCards/ReleaseInfoCard';
import { MinimalUser } from '@components/basics/MinimalUser';
import { Button } from '@components/main-components/button/Button';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { Marquee } from '@components/main-components/marquee/Marquee';
import { PlayButton } from '@components/main-components/play-button/PlayButton';
import { CollectModal } from '@components/modals/Collect/CollectModal';
import { ReleaseMintsModal } from '@components/modals/ReleaseMintsModal';
import { ModalType, useModal } from '@context/modal';
import { useSubscription } from '@graphql/graphql-ws';
import type { FragmentType } from '@graphql/reactQuery';
import {
  getFragment,
  gql,
  LandingHeroPlayReleaseFragmentDoc,
  MinimalReleaseUpdatesDocument,
} from '@graphql/reactQuery';
import { useCurrentListeningParty, usePlayButton } from '@hooks/usePlayButton';
import { trackClickEvent } from '@lib/analytics';
import { setUpNextTrackIds } from '@store/audio/AudioQueue';
import { getSingularOrPlural } from '@utils/helpers';
import { webappUriPath } from '@utils/url';
import { formatCountdown, getPercentageMinted } from './helpers';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment LandingHeroPlayRelease on Release {
    id
    type
    track {
      id
    }
    title
    webappUri
    mintStartTimestamp
    supplyCutoffTimestamp

    finalQuantity
    quantityLowerBound
    quantityUpperBound
    totalMinted
    listeningParty

    listing {
      id
      ... on TieredEditionListing {
        tiers {
          id
          tierNumber
          totalMinted
        }
      }
    }

    artist {
      id
      user {
        id
        ...MinimalUser
      }
    }
    ...CollectModal
  }
`);

function Play({
  trackId,
  releaseId,
  isAlbum,
  getNextTrackIds,
  listeningParty,
}: {
  trackId: string;
  releaseId: string;
  isAlbum: boolean;
  getNextTrackIds: () => string[];
  listeningParty: Maybe<number>;
}) {
  const {
    onPlayClick,
    isLoading,
    isPlaying,
    ref: playButtonPrefetchRef,
    currentListeningParty,
  } = usePlayButton({
    isAlbum,
    trackId,
    listeningParty,
    releaseId,
  });

  const onPlayButtonClick = useStableCallback(async () => {
    await onPlayClick();
    setUpNextTrackIds(getNextTrackIds());
  });

  return (
    <>
      <PlayButton
        ref={playButtonPrefetchRef}
        className="block md:hidden"
        playing={isPlaying}
        loading={isLoading}
        onClick={onPlayButtonClick}
        color="white"
        size="s"
        currentListeningParty={currentListeningParty}
      />
      <PlayButton
        ref={playButtonPrefetchRef}
        className="hidden md:block lt:hidden"
        playing={isPlaying}
        loading={isLoading}
        onClick={onPlayButtonClick}
        color="white"
        size="l"
        currentListeningParty={currentListeningParty}
      />
      <PlayButton
        ref={playButtonPrefetchRef}
        className="hidden lt:block"
        playing={isPlaying}
        loading={isLoading}
        onClick={onPlayButtonClick}
        color="white"
        size="xl"
        currentListeningParty={currentListeningParty}
      />
    </>
  );
}

function StartOrEndCountdown({
  mintStartTimestamp,
  supplyCutoffTimestamp,
  totalMinted,
  limitedMinted,
  finalQuantity,
  quantityLowerBound,
  quantityUpperBound,
}: {
  mintStartTimestamp: number;
  supplyCutoffTimestamp: number | null;
  totalMinted: number;
  limitedMinted: number;
  finalQuantity: number | null;
  quantityLowerBound: number;
  quantityUpperBound: number;
}) {
  const now = Date.now();
  const isAfterMintStart = now > mintStartTimestamp;

  const saleEndCountdown = useTimer({
    expiryTimestamp: supplyCutoffTimestamp ?? now,
    autoStart: false,
  });
  const saleStartCountdown = useTimer({
    expiryTimestamp: mintStartTimestamp,
    onExpire: () => {
      if (supplyCutoffTimestamp) saleEndCountdown.start();
    },
  });
  const formattedStartCountDown = formatCountdown({ ...saleStartCountdown, prefix: 'Starts in' });
  const formattedEndCountDown = formatCountdown({ ...saleEndCountdown, prefix: 'Ends in' });
  const percentageMinted = getPercentageMinted({
    limitedMinted,
    quantityUpperBound,
  });
  const shouldShowCountdown = shouldShowEndsInCountdown({
    finalQuantity,
    quantityUpperBound,
    quantityLowerBound,
    mintStartTimestamp,
  });

  let fomoText: string | null = null;
  if (shouldShowCountdown) {
    fomoText = formattedEndCountDown;
  } else if (Math.round((totalMinted / quantityUpperBound) * 100) >= 1) {
    fomoText = percentageMinted;
  }

  if (!isAfterMintStart) {
    return (
      <>
        <span className="opacity-50 md:hidden">•</span>
        <span className="text-fuchsia500 md:hidden">{formattedStartCountDown}</span>
      </>
    );
  }
  if (isAfterMintStart && !!totalMinted && fomoText) {
    return (
      <>
        <span className="opacity-50">•</span>
        <span className={shouldShowCountdown ? 'text-fuchsia500' : 'text-white'}>{fomoText}</span>
      </>
    );
  }

  return null;
}

export const PlayAndReleaseInfo = ({
  release,
  getNextTrackIds,
}: {
  release: FragmentType<LandingHeroPlayReleaseFragmentDoc>;
  getNextTrackIds: () => string[];
}) => {
  const releaseFrag = getFragment(LandingHeroPlayReleaseFragmentDoc, release);

  const { openModal } = useModal();

  const {
    id: releaseId,
    webappUri,
    mintStartTimestamp,
    supplyCutoffTimestamp,
    quantityLowerBound,
    quantityUpperBound,
    title,
    artist: { user },
    type: releaseType,
    track: { id: trackId },
    listing,
    listeningParty,
  } = releaseFrag;

  const { data: subscriptionData } = useSubscription({
    query: MinimalReleaseUpdatesDocument,
    variables: {
      id: releaseId,
    },
  });

  const { totalMinted, finalQuantity } = subscriptionData?.data.releaseUpdates || releaseFrag;

  // limitedMinted is used for percent remaining
  const vipMinted = listing?.tiers.find(t => t.tierNumber === 1)?.totalMinted;
  const limitedMinted = vipMinted != null ? vipMinted : totalMinted;

  const pathnameUri = webappUriPath(webappUri);

  const isAfterMintStart = isPast(mintStartTimestamp);

  const currentListeningParty = useCurrentListeningParty({
    listeningParty,
    releaseId,
  });

  return (
    <>
      <div className="flex min-w-0 gap-4">
        <Play
          trackId={trackId}
          isAlbum={releaseType === 'ALBUM'}
          getNextTrackIds={getNextTrackIds}
          listeningParty={listeningParty}
          releaseId={releaseId}
        />
        <div className="flex min-w-0 flex-grow flex-col justify-between md:h-[60px] lt:h-[76px]">
          <Marquee>
            <h2 className="w-fit font-title text-title-m font-medium md:text-title-xl lt:text-title-xxl">
              <AccessibleLink pathnameUri={pathnameUri}>{title}</AccessibleLink>
            </h2>
          </Marquee>
          <div className="align-center flex gap-2 text-base-s text-white [&_.username:hover]:text-white [&_.username]:text-base-s [&_.username]:font-normal [&_.username]:text-base300">
            <MinimalUser hideAvatar hideVerified userFrag={user} imagePriority={false} />
            {!!totalMinted && (
              <>
                <span className="hidden opacity-50 md:inline">•</span>
                <button
                  className="hidden text-base300 hover:text-white md:inline"
                  onClick={() => {
                    openModal(ModalType.RELEASE_MINTS, {
                      body: <ReleaseMintsModal releaseId={releaseId} />,
                    });
                  }}
                >
                  {millify(totalMinted)} {getSingularOrPlural({ count: totalMinted, word: 'Mint' })}
                </button>
              </>
            )}
            <StartOrEndCountdown
              {...{
                mintStartTimestamp,
                supplyCutoffTimestamp,
                totalMinted,
                limitedMinted,
                finalQuantity,
                quantityLowerBound,
                quantityUpperBound,
              }}
            />
          </div>
        </div>
      </div>
      {(listeningParty != null ? currentListeningParty == null : isAfterMintStart) ? (
        <Button
          className="mt-7 hidden md:flex"
          onClick={() => {
            trackClickEvent({
              event: 'Collect Now Clicked',
              properties: {
                sourceComponent: 'Landing Hero',
                associationType: 'Release',
                associationId: releaseId,
              },
            });
            openModal(ModalType.COLLECT, {
              body: (
                <CollectModal
                  mintReferral={null}
                  release={releaseFrag}
                  collectButtonSource="Landing Hero"
                />
              ),
            });
          }}
          variant="fuchsia"
          label="Collect Now"
          // @ts-expect-error: TS does not like the responsive variants here
          size={{ '@initial': 'M', '@laptop': 'L' }}
        />
      ) : (
        <div className="hidden md:block">
          <span className="mb-2 mt-7 block text-base-xs uppercase opacity-80 lt:text-base-s">
            {listeningParty != null ? '🎧 Listening Party Begins...' : 'Release Party Begins...'}
          </span>
          <div className="max-w-[230px] [&>*]:m-0 [&_div]:text-numerals-xs lt:[&_div]:text-numerals-s [&_p]:text-numerals-xs lt:[&_p]:text-numerals-s">
            <HeroCountdown
              timestamp={
                listeningParty != null
                  ? currentListeningParty?.listeningParty ?? 0
                  : mintStartTimestamp
              }
            />
          </div>
          <GetNotifiedButton
            type="landing_hero"
            artistId={releaseFrag.artist.id}
            userId={releaseFrag.artist.user.id}
          />
        </div>
      )}
    </>
  );
};
